// packages/shared/api-client/endpoints/notifications.js
// Bulk notification and preferences API endpoints

import { baseApi } from "../baseApi";

/**
 * Notification API endpoints
 * Provides bulk email/SMS/push notifications and user preferences management
 */
const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Send bulk email
    sendEmail: builder.mutation({
      query: (emailData) => ({
        url: '/api/notifications/send-email',
        method: 'POST',
        body: emailData, // { recipients, subject, body, ... }
      }),
    }),

    // Send bulk SMS
    sendSms: builder.mutation({
      query: (smsData) => ({
        url: '/api/notifications/send-sms',
        method: 'POST',
        body: smsData, // { recipients, message, ... }
      }),
    }),

    // Send bulk push notification
    sendPush: builder.mutation({
      query: (pushData) => ({
        url: '/api/notifications/send-push',
        method: 'POST',
        body: pushData, // { recipients, title, body, data, ... }
      }),
    }),

    // Send multi-channel notification (unified)
    sendNotification: builder.mutation({
      query: (notificationData) => ({
        url: '/api/notifications/send',
        method: 'POST',
        body: notificationData, // { channels, recipients, ... }
      }),
    }),

    // Get recipient count estimate
    getRecipientCount: builder.mutation({
      query: (filters) => ({
        url: '/api/notifications/recipient-count',
        method: 'POST',
        body: filters, // { audience, departmentIds, roleIds, usePreferences, channels }
      }),
    }),

    // Update notification preferences for current user
    updateNotificationPreferences: builder.mutation({
      query: (preferences) => ({
        url: '/api/notifications/preferences',
        method: 'PUT',
        body: preferences,
      }),
      invalidatesTags: ['User'], // Invalidate user cache as preferences are part of user data
    }),

    // Get notification preferences for current user
    getNotificationPreferences: builder.query({
      query: () => ({
        url: '/api/notifications/preferences',
        method: 'GET',
      }),
      providesTags: ['NotificationPreferences'],
    }),

    // Get unread posts for current user
    getUnreadPosts: builder.query({
      query: () => ({
        url: '/api/notifications/unread-posts',
        method: 'GET',
      }),
      providesTags: ['UnreadPosts'],
    }),

    // Get unread events for current user
    getUnreadEvents: builder.query({
      query: () => ({
        url: '/api/notifications/unread-events',
        method: 'GET',
      }),
      providesTags: ['UnreadEvents'],
    }),

    // Get unread tasks for current user
    getUnreadTasks: builder.query({
      query: () => ({
        url: '/api/notifications/unread-tasks',
        method: 'GET',
      }),
      providesTags: ['UnreadTasks'],
    }),

    // Mark posts as read
    markPostsAsRead: builder.mutation({
      query: () => ({
        url: '/api/notifications/mark-posts-read',
        method: 'POST',
      }),
      invalidatesTags: ['UnreadPosts'], // Invalidate unread posts cache
    }),

    // Mark events as read
    markEventsAsRead: builder.mutation({
      query: () => ({
        url: '/api/notifications/mark-events-read',
        method: 'POST',
      }),
      invalidatesTags: ['UnreadEvents'], // Invalidate unread events cache
    }),

    // Mark tasks as read
    markTasksAsRead: builder.mutation({
      query: () => ({
        url: '/api/notifications/mark-tasks-read',
        method: 'POST',
      }),
      invalidatesTags: ['UnreadTasks'], // Invalidate unread tasks cache
    }),

    // Toggle weekly digest opt-in/out
    toggleWeeklyDigest: builder.mutation({
      query: (data) => ({
        url: '/api/notifications/weekly-digest',
        method: 'POST',
        body: data, // { optIn: boolean }
      }),
      invalidatesTags: ['NotificationPreferences'], // Invalidate preferences cache
    }),

    // Get presigned S3 URL for email attachment upload
    getEmailAttachmentUploadUrl: builder.mutation({
      query: (data) => ({
        url: '/api/notifications/attachment-upload-url',
        method: 'POST',
        body: data, // { fileName, fileType, fileSize, blastId }
      }),
    }),

    // Get email blast history
    getEmailBlasts: builder.query({
      query: (params) => ({
        url: '/api/notifications/blasts',
        method: 'GET',
        params, // { page, limit }
      }),
      providesTags: ['EmailBlast'],
    }),

    // Get unified blast history (email + SMS + push)
    getBlastHistory: builder.query({
      query: (params) => ({
        url: '/api/notifications/blast-history',
        method: 'GET',
        params, // { page, limit, channel }
      }),
      providesTags: ['BlastHistory'],
    }),
  }),
});

// Export hooks for usage in components
export const useSendEmailMutation = notificationApi.useSendEmailMutation;
export const useSendSmsMutation = notificationApi.useSendSmsMutation;
export const useSendPushMutation = notificationApi.useSendPushMutation;
export const useSendNotificationMutation = notificationApi.useSendNotificationMutation;
export const useGetRecipientCountMutation = notificationApi.useGetRecipientCountMutation;
export const useUpdateNotificationPreferencesMutation = notificationApi.useUpdateNotificationPreferencesMutation;
export const useGetNotificationPreferencesQuery = notificationApi.useGetNotificationPreferencesQuery;
export const useGetUnreadPostsQuery = notificationApi.useGetUnreadPostsQuery;
export const useGetUnreadEventsQuery = notificationApi.useGetUnreadEventsQuery;
export const useGetUnreadTasksQuery = notificationApi.useGetUnreadTasksQuery;
export const useMarkPostsAsReadMutation = notificationApi.useMarkPostsAsReadMutation;
export const useMarkEventsAsReadMutation = notificationApi.useMarkEventsAsReadMutation;
export const useMarkTasksAsReadMutation = notificationApi.useMarkTasksAsReadMutation;
export const useToggleWeeklyDigestMutation = notificationApi.useToggleWeeklyDigestMutation;
export const useGetEmailAttachmentUploadUrlMutation = notificationApi.useGetEmailAttachmentUploadUrlMutation;
export const useGetEmailBlastsQuery = notificationApi.useGetEmailBlastsQuery;
export const useGetBlastHistoryQuery = notificationApi.useGetBlastHistoryQuery;
